/* eslint-disable i18next/no-literal-string */
import { useTranslations } from '@aikoa/i18n/react'
import { Pressable, View } from 'react-native'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'

export type IntervalUnit = 'days' | 'weeks' | 'months' | 'years'

const INTERVAL_UNITS: IntervalUnit[] = ['days', 'weeks', 'months', 'years']

export type IntervalValue = {
  value: string
  unit: IntervalUnit
}

export function formatIntervalToPostgres(interval: IntervalValue): string | null {
  const numValue = Number.parseInt(interval.value, 10)

  if (Number.isNaN(numValue) || numValue <= 0) {
    return null
  }

  return `${numValue} ${interval.unit}`
}

export function parsePostgresInterval(intervalString: string | null | undefined): IntervalValue {
  if (!intervalString || typeof intervalString !== 'string') {
    return { value: '', unit: 'months' }
  }

  const normalized = intervalString.toLowerCase().trim()

  const yearsMatch = normalized.match(/(\d+)\s*years?/)
  if (yearsMatch) {
    return { value: yearsMatch[1], unit: 'years' }
  }

  const monsMatch = normalized.match(/(\d+)\s*mons?/)
  const monthsMatch = normalized.match(/(\d+)\s*months?/)
  if (monsMatch) {
    return { value: monsMatch[1], unit: 'months' }
  }

  if (monthsMatch) {
    return { value: monthsMatch[1], unit: 'months' }
  }

  const daysMatch = normalized.match(/(\d+)\s*days?/)
  if (daysMatch) {
    const days = Number.parseInt(daysMatch[1], 10)

    if (days % 7 === 0 && days > 0) {
      return { value: String(days / 7), unit: 'weeks' }
    }

    return { value: daysMatch[1], unit: 'days' }
  }

  const weeksMatch = normalized.match(/(\d+)\s*weeks?/)
  if (weeksMatch) {
    return { value: weeksMatch[1], unit: 'weeks' }
  }

  return { value: '', unit: 'months' }
}

type IntervalInputProps = {
  value: IntervalValue
  onChange: (value: IntervalValue) => void
}

export function IntervalInput({ value, onChange }: IntervalInputProps) {
  const { t } = useTranslations('admin')

  return (
    <View className="flex-row gap-2">
      <Input
        value={value.value}
        onChangeText={(text) => onChange({ ...value, value: text })}
        placeholder="0"
        keyboardType="number-pad"
        variant="box"
        className="flex-1 bg-white"
      />

      <View className="flex-row rounded-lg border border-neutral-200 bg-white">
        {INTERVAL_UNITS.map((unit) => (
          <Pressable
            key={unit}
            onPress={() => onChange({ ...value, unit })}
            className={`px-3 py-2.5 ${value.unit === unit ? 'rounded-lg bg-primary' : ''}`}>
            <Text
              className={`text-sm ${value.unit === unit ? 'font-medium text-white' : 'text-neutral-600'}`}>
              {t(($) => $.promotions.intervalUnits[unit])}
            </Text>
          </Pressable>
        ))}
      </View>
    </View>
  )
}
